/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import DashboardLayout from "pages/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/Navbars/DashboardNavbar";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import RestoreIcon from "@mui/icons-material/Restore";
import useFetchWithMsal from "hooks/useFetchWithMsal";
import { protectedResources, siteData } from "authConfig";
import { useNavigate, useLocation } from "react-router-dom";
import { useScreenContext } from "context/ScreenContext";
import DualPlaneRadar from "./DualPlaneRadar";

const API = `${protectedResources.apiTodoList.endpoint}/emergingRisk/radar-data`;
const EVAL_API = `${protectedResources.apiTodoList.endpoint}/evaluation-criteria/config`; // ID172
const CATEGORIES_API = `${protectedResources.apiTodoList.endpoint}/getAllCategoriesSubCategories`;
const LIST_API = `${protectedResources.apiTodoList.endpoint}/emergingRisk/list`;
const RESTORE_API = `${protectedResources.apiTodoList.endpoint}/emergingRisk/restore`;

// ── SVG geometry constants ────────────────────────────────────────────────────
const W = 760;
const H = 440;
const CX = 380; // centre X
const CY = 300; // centre Y (offset down for perspective)
const RX = 320; // outer ring X radius
// RY is now dynamic (computed from tilt angle rotX). Default tilt ≈ 20° → RY ≈ 112.
const DEFAULT_ROT_X = Math.asin(112 / 320); // ≈ 0.357 rad — matches original look
// Crop the empty band above the content (oval + labels + bars start ~y=110)
const VIEW_TOP = 80;

// Point on the tilted ellipse. ryEff = RX * sin(rotX), can be negative when flipped.
function ptOnEllipse(angle, rFraction, ryEff = 112) {
  return {
    x: CX + rFraction * RX * Math.cos(angle),
    y: CY + rFraction * ryEff * Math.sin(angle),
  };
}

const RINGS = [
  { label: "Near", r: 0.33 },
  { label: "Medium", r: 0.66 },
  { label: "Long", r: 1.0 },
];

function timeFrameR(tf) {
  if (!tf) return 0.66;
  if (tf.includes("near")) return 0.28;
  if (tf.includes("long")) return 0.95;
  return 0.62;
}

// Velocity offset within the horizon band: fast = front edge (inner, toward now),
// slow = back edge (outer). Velocity is stored as "… + High" / "… + Low".
function velocityROffset(v) {
  if (!v) return 0;
  const l = v.toLowerCase();
  if (l.includes("fast") || l.includes("high")) return -0.07;
  if (l.includes("slow") || l.includes("low")) return 0.07;
  return 0;
}

// ── Colour helpers ────────────────────────────────────────────────────────────
const THREAT_COLOR = "#ef4444";
const OPP_COLOR = "#3b82f6";
const ALERT_COLORS = { red: "#dc2626", amber: "#d97706", none: "#9ca3af" };

function alertIcon(alert, dir) {
  if (alert === "red" || dir === "triggered") return "⚠";
  if (alert === "amber" || dir === "imminent") return "⚑";
  if (dir === "approaching") return "⚑";
  return null;
}

// ── Format money ──────────────────────────────────────────────────────────────
function fmtMoney(v) {
  if (!v) return "—";
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return "—";
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

// ── ID151: sort helpers for the risk table beneath the radar ───────────────────
const TIMEFRAME_ORDER = { "near-term": 1, "medium-term": 2, "long-term": 3 };
const SP_DIR_ORDER = { triggered: 3, imminent: 2, approaching: 1 };
const ALERT_ORDER = { red: 2, amber: 1 };
const riskSortValue = (r, col) => {
  if (col === "risk") return String(r.title || "").toLowerCase();
  if (col === "timeframe") return TIMEFRAME_ORDER[r.timeFrame] || 0;
  if (col === "velocity") {
    const v = String(r.velocity || "").toLowerCase();
    return v.includes("fast") ? 2 : v.includes("slow") ? 1 : 0;
  }
  if (col === "signposts") return SP_DIR_ORDER[r.signpostDirection] || 0;
  if (col === "alert") return ALERT_ORDER[r.signpostAlert] || 0;
  return 0;
};
function sortRiskRows(rows, sort) {
  if (!sort.col) return rows;
  return [...rows].sort((a, b) => {
    const va = riskSortValue(a, sort.col);
    const vb = riskSortValue(b, sort.col);
    const c = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb));
    return sort.dir === "asc" ? c : -c;
  });
}
function SortableTh({ col, sort, onSort, children }) {
  const active = sort.col === col;
  return (
    <TableCell
      sx={{ fontWeight: 700, color: "#52009b", fontSize: "0.75rem" }}
      sortDirection={active ? sort.dir : false}
    >
      <TableSortLabel
        active={active}
        direction={active ? sort.dir : "asc"}
        onClick={() => onSort(col)}
        sx={{
          color: "#52009b !important",
          "& .MuiTableSortLabel-icon": { color: "#52009b !important" },
        }}
      >
        {children}
      </TableSortLabel>
    </TableCell>
  );
}
SortableTh.propTypes = {
  col: PropTypes.string.isRequired,
  sort: PropTypes.shape({ col: PropTypes.string, dir: PropTypes.string }).isRequired,
  onSort: PropTypes.func.isRequired,
  children: PropTypes.node,
};
SortableTh.defaultProps = { children: null };

// ── Main component ────────────────────────────────────────────────────────────
// Props:
//   embedded   – when true, skip DashboardLayout (for use inside another page)
//   onOpenRisk – called with the risk object when user clicks a dot or table row
//   onCreateRisk – called when user clicks "Add Risk"
export default function EmergingRiskRadar({ embedded = false, onOpenRisk, onCreateRisk }) {
  const { execute } = useFetchWithMsal({ scopes: [siteData?.apiScopes] });
  const executeRef = useRef(execute);
  useEffect(() => {
    executeRef.current = execute;
  });

  const navigate = useNavigate();

  const [risks, setRisks] = useState([]); // active risks only — drives the radar plot
  const [draftRisks, setDraftRisks] = useState([]); // drafts — shown in the table when toggled on
  const [showDrafts, setShowDrafts] = useState(false); // ID86: hide drafts by default; toggle to include
  const [showArchived, setShowArchived] = useState(false); // ID195: reveal archived (closed) risks + restore
  const [archivedRisks, setArchivedRisks] = useState([]);
  // ID151 — sort the risk table beneath the radar by any column heading. col=null = default order.
  const [tableSort, setTableSort] = useState({ col: null, dir: "asc" });
  const handleTableSort = (col) =>
    setTableSort((s) => ({ col, dir: s.col === col && s.dir === "asc" ? "desc" : "asc" }));
  const [taxonomy, setTaxonomy] = useState([]); // [{ Category, SubCategory: [...] }]
  // ID172 — plausibility mode + likelihood bands drive the radar rings (timeframe vs likelihood).
  const [plausibilityMode, setPlausibilityMode] = useState("timeframe_velocity");
  const [likelihoodBands, setLikelihoodBands] = useState([]);
  const [consequenceLevels, setConsequenceLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null); // clicked risk id
  const [hovered, setHovered] = useState(null); // hovered risk id
  const [previewId, setPreviewId] = useState(null); // default-previewed risk (keeps the panel populated)
  // Dual-plane is the only visible view now; the classic 3-D radar code is kept but no longer
  // exposed via a toggle. Flip this back to "classic" (and restore the toggle) to bring it back.
  const [viewMode] = useState("dual"); // "classic" 3-D radar | "dual" plan+profile

  // Drag-to-rotate: rotZ = spin (X-drag), rotX = tilt/flip (Y-drag)
  const [rotZ, setRotZ] = useState(0);
  const [rotX, setRotX] = useState(DEFAULT_ROT_X);
  const dragRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startRotZ: 0,
    startRotX: DEFAULT_ROT_X,
  });
  const radarBoxRef = useRef(null); // for native non-passive wheel listener
  const svgRef = useRef(null); // to map cursor → SVG coords for zoom

  // Zoom: continuous scale factor (1 = fit, up to 12×) + pan offset, for separating close balls.
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const ZOOM_MIN = 1;
  const ZOOM_MAX = 12;
  const FULL_H = H - VIEW_TOP;
  const viewRef = useRef({ scale: 1, panX: 0, panY: 0 });

  // Zoom by a multiplicative factor, keeping the SVG point under (clientX, clientY) fixed.
  // When client coords are omitted, anchors on the current view centre (used by the buttons).
  const zoomToward = useCallback(
    (factor, clientX, clientY) => {
      const v = viewRef.current;
      const ns = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, +(v.scale * factor).toFixed(3)));
      if (ns === v.scale) return;
      const el = svgRef.current || radarBoxRef.current;
      const rect = el ? el.getBoundingClientRect() : null;
      const fx = rect && clientX != null && rect.width ? (clientX - rect.left) / rect.width : 0.5;
      const fy = rect && clientY != null && rect.height ? (clientY - rect.top) / rect.height : 0.5;
      const curVbW = W / v.scale;
      const curVbH = FULL_H / v.scale;
      const curLeft = CX - curVbW / 2 + v.panX;
      const curTop = VIEW_TOP + FULL_H / 2 - curVbH / 2 + v.panY;
      const svgX = curLeft + fx * curVbW;
      const svgY = curTop + fy * curVbH;
      const newVbW = W / ns;
      const newVbH = FULL_H / ns;
      setScale(ns);
      setPan({
        x: svgX - fx * newVbW - (CX - newVbW / 2),
        y: svgY - fy * newVbH - (VIEW_TOP + FULL_H / 2 - newVbH / 2),
      });
    },
    [FULL_H]
  );

  const zoomBy = (factor) => zoomToward(factor); // centre-anchored (toolbar buttons)
  const resetView = () => {
    setScale(1);
    setPan({ x: 0, y: 0 });
  };

  // Mouse-wheel zoom toward the cursor (non-passive so preventDefault works).
  useEffect(() => {
    const el = radarBoxRef.current;
    if (!el) return undefined;
    const onWheelNative = (e) => {
      e.preventDefault();
      zoomToward(e.deltaY < 0 ? 1.3 : 1 / 1.3, e.clientX, e.clientY);
    };
    el.addEventListener("wheel", onWheelNative, { passive: false });
    return () => el.removeEventListener("wheel", onWheelNative);
    // viewMode included so the listener re-attaches to the fresh Box after a view toggle
    // (otherwise zoom silently stops working when switching back to Radar 3D).
  }, [loading, error, zoomToward, viewMode]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, catRes, evalRes] = await Promise.all([
        executeRef.current("GET", `${API}?includeDrafts=1`),
        executeRef.current("GET", CATEGORIES_API),
        executeRef.current("GET", EVAL_API),
      ]);
      // ID172 — radar mode + likelihood bands (ordered furthest-out → centre).
      const cfg = evalRes?.data ?? evalRes ?? {};
      setPlausibilityMode(
        cfg?.plausibilityMode === "likelihood" ? "likelihood" : "timeframe_velocity"
      );
      setLikelihoodBands(
        (cfg?.likelihoodTable?.rows || [])
          .slice()
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((r) => r.rating)
          .filter(Boolean)
      );
      setConsequenceLevels(
        (cfg?.consequenceTable?.levels || []).map((l) => l.label).filter(Boolean)
      );
      const all = Array.isArray(res?.data) ? res.data : [];
      // Radar plots active risks only; drafts are surfaced in the Watch List table.
      const activeRisks = all.filter((r) => r.status === "active");
      setRisks(activeRisks);
      // Default-preview the top (highest-priority) risk so the detail panel isn't empty on load.
      setPreviewId((cur) => cur || activeRisks[0]?.id || null);
      setDraftRisks(all.filter((r) => r.status !== "active"));
      const cats = Array.isArray(catRes) ? catRes : catRes?.data || [];
      setTaxonomy(Array.isArray(cats) ? cats : []);
    } catch {
      setError("Failed to load radar data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ID195 — archived (closed) risks for the "Show archived" panel; loaded up front so the toggle
  // advertises the count, and reloaded after a restore.
  const loadArchived = useCallback(() => {
    executeRef
      .current("GET", `${LIST_API}?status=archived`)
      .then((res) => {
        const arr = Array.isArray(res?.data) ? res.data : [];
        // The list endpoint returns raw records keyed by _id; normalise to `id` for the UI.
        // eslint-disable-next-line no-underscore-dangle
        setArchivedRisks(arr.map((d) => ({ ...d, id: d.id || d._id })));
      })
      .catch(() => setArchivedRisks([]));
  }, []);
  useEffect(() => {
    loadArchived();
  }, [loadArchived]);
  const handleRestore = useCallback(
    (id) => {
      if (!id) return;
      executeRef
        .current("PATCH", `${RESTORE_API}/${id}`)
        .then(() => {
          load();
          loadArchived();
        })
        .catch(() => {});
    },
    [load, loadArchived]
  );

  // All L1 categories from the taxonomy (shown as spokes even with no linked risk),
  // plus any risk categories not present in the taxonomy.
  const taxonomyCats = taxonomy.map((t) => t.Category).filter(Boolean);
  const riskCats = risks.map((r) => r.category).filter(Boolean);
  const categories = [...new Set([...taxonomyCats, ...riskCats])];

  const catAngle = (cat) => {
    const idx = categories.indexOf(cat);
    if (idx < 0) return 0;
    return (idx / Math.max(categories.length, 1)) * 2 * Math.PI - Math.PI / 2;
  };

  // Lay out risks: angle = category spoke; radius = horizon band ± velocity offset.
  // Risks that land on the same spot (same sub-category angle + same radius) are
  // spread in 2-D (around the arc AND in/out by distance) so they don't stack.
  const layout = (() => {
    const base = risks.map((r) => ({
      angle: catAngle(r.category),
      rFrac: timeFrameR(r.timeFrame) + velocityROffset(r.velocity),
    }));
    const groups = {};
    base.forEach((b, i) => {
      const key = `${b.angle.toFixed(3)}|${b.rFrac.toFixed(2)}`;
      (groups[key] = groups[key] || []).push(i);
    });
    const out = base.map((b) => ({ ...b }));
    Object.values(groups).forEach((idxs) => {
      if (idxs.length < 2) return;
      const mid = (idxs.length - 1) / 2;
      idxs.forEach((idx, gi) => {
        const k = gi - mid; // …,-1,0,1,… → diagonal scatter
        out[idx].angle += k * 0.16;
        out[idx].rFrac = Math.max(0.12, Math.min(1.12, out[idx].rFrac + k * 0.06));
      });
    });
    return out;
  })();
  const angles = layout.map((p) => p.angle);

  // L2-on-zoom: one representative risk per (L1 || L2) pair carries the sub-category label, so a
  // zoomed-in radar reveals L2 categories without a label on every ball. (Focus mode shows the
  // focused risk's L2 separately.)
  const l2RepRiskIds = (() => {
    const seen = new Set();
    const reps = new Set();
    risks.forEach((r) => {
      const sub = String(r.subCategory || "").trim();
      if (!sub) return;
      const key = `${r.category || ""}||${sub}`;
      if (!seen.has(key)) {
        seen.add(key);
        reps.add(r.id);
      }
    });
    return reps;
  })();

  // Max consequence for bar scaling
  const maxVal = Math.max(
    ...risks.flatMap((r) => [r.scenarios.optimistic.value, r.scenarios.pessimistic.value]),
    1
  );
  const BAR_MAX_H = 56; // px at max consequence
  const MIN_BAR_H = 12; // px floor so small-consequence risks still show a visible bar

  // ── Mouse handlers for drag rotation ─────────────────────────────────────
  // X-drag spins the radar (rotZ); Y-drag tilts/flips the plane (rotX).
  const onMouseDown = (e) => {
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      startRotZ: rotZ,
      startRotX: rotX,
    };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setRotZ(dragRef.current.startRotZ + dx * 0.007);
    setRotX(dragRef.current.startRotX + dy * 0.01);
  };
  const onMouseUp = () => {
    dragRef.current.active = false;
  };

  // Dynamic geometry from current tilt angle
  const ryEff = RX * Math.sin(rotX); // can go negative (flip)
  const barDir = Math.cos(rotX); // 1=up, -1=upside-down, 0=edge-on
  // Convenience point function using live rotX
  const pt = (angle, r) => ptOnEllipse(angle, r, ryEff);

  // Current viewBox bounds — used to decide which categories show L2 labels.
  const fullH = H - VIEW_TOP;
  const vbW = W / scale;
  const vbH = fullH / scale;
  const vbLeft = CX - vbW / 2 + pan.x;
  const vbRight = CX + vbW / 2 + pan.x;
  const vbTop = VIEW_TOP + fullH / 2 - vbH / 2 + pan.y;
  const vbBot = VIEW_TOP + fullH / 2 + vbH / 2 + pan.y;
  // Keep a live snapshot for the (once-attached) wheel listener.
  viewRef.current = { scale, panX: pan.x, panY: pan.y };
  // Returns true if a point (with a small margin) falls inside the current viewBox.
  const inView = (px, py, margin = 30) =>
    px >= vbLeft - margin && px <= vbRight + margin && py >= vbTop - margin && py <= vbBot + margin;

  // Falls back to the default-previewed risk so the detail panel always shows something.
  const activeRisk =
    [...risks, ...draftRisks].find((r) => r.id === (selected || hovered || previewId)) || null;

  // Screen-aware Risk Advisor — publish the visible watchlist risks + the focused risk.
  const { setScreenContext } = useScreenContext();
  const scLocation = useLocation();
  useEffect(() => {
    const list = (risks || []).slice(0, 30).map((r) => ({
      id: r.id,
      title: r.title,
      category: r.category,
      subCategory: r.subCategory,
      value: r.severity,
      alert: r.signpostAlert,
      trend: r.signpostDirection,
      likelihood: r.likelihood || undefined,
    }));
    setScreenContext({
      route: scLocation.pathname,
      screenName: "Watchlist Risks",
      data: {
        riskCount: (risks || []).length,
        focusedRisk: activeRisk
          ? { id: activeRisk.id, title: activeRisk.title, category: activeRisk.category }
          : null,
        risks: list,
      },
    });
  }, [risks, activeRisk, scLocation.pathname, setScreenContext]);

  // Focus mode (a dot is clicked): keep only the focused risk's L1 category spoke; reveal its
  // L2 (sub-category) once zoomed in. (Export ignores focus at the usage sites below.)
  const focusRisk = selected ? risks.find((r) => r.id === selected) : null;
  const focusCat = focusRisk?.category || null;

  // Clicking a risk pins the detail card open on the right (toggle).
  // Navigation to the full record only happens from the "Open full record" button.
  const handleRiskClick = (risk) => {
    setSelected(selected === risk.id ? null : risk.id);
  };

  // Export-mode re-renders the radar with numbered balls + no titles, then captures it.
  const [exporting, setExporting] = useState(false);

  // Build the PNG: full radar (numbered balls, no titles) + legend + a numbered risk list.
  const runExportCapture = useCallback(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const exportScale = 2; // hi-res output
    const titleH = 34;
    const legendH = 96;

    // Full radar with generous side margins so the (un-truncated) category labels never clip.
    const padX = 210;
    const vbX = -padX;
    const vbY = VIEW_TOP - 12;
    const w = W + padX * 2;
    const h = FULL_H + 24;

    const clone = svg.cloneNode(true);
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clone.setAttribute("viewBox", `${vbX} ${vbY} ${w} ${h}`);
    clone.setAttribute("width", String(w));
    clone.setAttribute("height", String(h));
    // Un-truncate the category labels still present (risk titles are hidden in export mode).
    const fullLabels = clone.querySelectorAll("text[data-fulllabel]");
    for (let i = 0; i < fullLabels.length; i += 1) {
      const node = fullLabels[i];
      const full = node.getAttribute("data-fulllabel");
      if (full) node.textContent = full;
    }
    const svgStr = new XMLSerializer().serializeToString(clone);
    const url = URL.createObjectURL(new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" }));

    // Numbered list — matches the ball numbers (displayNo), ordered by number.
    const listed = risks
      .filter((r) => r.displayNo != null)
      .slice()
      .sort((a, b) => a.displayNo - b.displayNo);

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Pre-measure the wrapped list so we can size the canvas to fit it.
      const listFont = "12px Arial, sans-serif";
      const listLineH = 16;
      const listX = 14;
      const listMaxW = w - 28;
      ctx.font = listFont;
      const wrap = (text) => {
        const words = String(text).split(/\s+/);
        const lines = [];
        let cur = "";
        words.forEach((word) => {
          const test = cur ? `${cur} ${word}` : word;
          if (ctx.measureText(test).width > listMaxW && cur) {
            lines.push(cur);
            cur = word;
          } else {
            cur = test;
          }
        });
        if (cur) lines.push(cur);
        return lines;
      };
      const listEntries = listed.map((r) => {
        const desc = r.description ? ` — ${String(r.description).slice(0, 220)}` : "";
        return wrap(`${r.displayNo}. ${r.title}${desc}`);
      });
      const listLineCount = listEntries.reduce((n, lines) => n + lines.length, 0);
      const listHeaderH = listed.length ? 26 : 0;
      const listH = listed.length
        ? listHeaderH + listLineCount * listLineH + listed.length * 4 + 12
        : 0;

      const totalH = titleH + h + legendH + listH;
      canvas.width = w * exportScale;
      canvas.height = totalH * exportScale;
      ctx.scale(exportScale, exportScale);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, totalH);

      // Title
      ctx.fillStyle = "#52009b";
      ctx.font = "bold 17px Arial, sans-serif";
      ctx.textBaseline = "middle";
      ctx.fillText("Watchlist Risk Radar", 12, titleH / 2);

      // Radar
      ctx.drawImage(img, 0, titleH, w, h);
      URL.revokeObjectURL(url);

      // Legend
      const items = [
        { swatch: THREAT_COLOR, text: "Threat / loss" },
        { swatch: OPP_COLOR, text: "Opportunity / gain" },
        { text: "⚠ signpost breached" },
        { text: "⚑ signpost approaching" },
        { text: "● current estimate (size = consequence)" },
        { swatch: "#f97316", text: "→ velocity (longer = faster)" },
        { text: "Bar top = best case · bottom = worst case" },
      ];
      ctx.font = "12px Arial, sans-serif";
      ctx.textBaseline = "middle";
      let lx = 12;
      let ly = titleH + h + 16;
      const lineGap = 22;
      const padRight = 18;
      items.forEach((it) => {
        const swW = it.swatch ? 14 : 0;
        const textW = ctx.measureText(it.text).width;
        const itemW = swW + textW + padRight;
        if (lx + itemW > w - 4 && lx > 12) {
          lx = 12;
          ly += lineGap;
        }
        if (it.swatch) {
          ctx.fillStyle = it.swatch;
          ctx.fillRect(lx, ly - 5, 10, 10);
          lx += swW;
        }
        ctx.fillStyle = "#555555";
        ctx.fillText(it.text, lx, ly);
        lx += textW + padRight;
      });

      // Numbered risk list (number = the ball number; with description)
      if (listed.length) {
        let y = titleH + h + legendH;
        ctx.fillStyle = "#52009b";
        ctx.font = "bold 13px Arial, sans-serif";
        ctx.fillText("Watchlist risks", listX, y);
        y += listHeaderH;
        ctx.font = listFont;
        ctx.fillStyle = "#374151";
        listEntries.forEach((lines) => {
          lines.forEach((ln) => {
            ctx.fillText(ln, listX, y);
            y += listLineH;
          });
          y += 4;
        });
      }

      canvas.toBlob((blob) => {
        if (!blob) return;
        const href = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = href;
        a.download = `watchlist-radar-${new Date().toISOString().slice(0, 10)}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(href);
      }, "image/png");
    };
    img.onerror = () => URL.revokeObjectURL(url);
    img.src = url;
  }, [risks]);

  // Trigger export = flip into export mode; capture on the next frame, then flip back.
  const handleExportImage = useCallback(() => setExporting(true), []);
  useEffect(() => {
    if (!exporting) return undefined;
    const id = requestAnimationFrame(() => {
      runExportCapture();
      setExporting(false);
    });
    return () => cancelAnimationFrame(id);
  }, [exporting, runExportCapture]);

  const content = (
    <Box sx={{ p: embedded ? 0 : 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: viewMode === "dual" ? 0.5 : 2,
        }}
      >
        {/* Title only when standalone — the embedding page already shows a "Watchlist Risks" heading. */}
        {embedded ? (
          <span />
        ) : (
          <MDTypography variant="h5" fontWeight="bold">
            Watchlist Risk Radar
          </MDTypography>
        )}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {viewMode === "classic" && (
            <MDButton
              size="small"
              variant="outlined"
              onClick={handleExportImage}
              sx={{
                color: "#52009b",
                borderColor: "#52009b",
                textTransform: "none",
                "&:hover": { borderColor: "#3a0070", bgcolor: "rgba(82,0,155,0.06)" },
              }}
            >
              Export image
            </MDButton>
          )}
          {/* Zoom controls — scroll the radar or use these buttons (classic only) */}
          {viewMode === "classic" && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                border: "1px solid rgba(82,0,155,0.3)",
                borderRadius: "6px",
                overflow: "hidden",
              }}
            >
              <Box
                onClick={() => zoomBy(1 / 1.4)}
                sx={{
                  px: 1.25,
                  py: 0.5,
                  fontSize: "1rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  color: "#52009b",
                  borderRight: "1px solid rgba(82,0,155,0.2)",
                  "&:hover": { bgcolor: "rgba(82,0,155,0.06)" },
                }}
              >
                −
              </Box>
              <Box
                sx={{
                  px: 1.25,
                  py: 0.5,
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  color: "#52009b",
                  minWidth: 44,
                  textAlign: "center",
                }}
              >
                {Math.round(scale * 100)}%
              </Box>
              <Box
                onClick={() => zoomBy(1.4)}
                sx={{
                  px: 1.25,
                  py: 0.5,
                  fontSize: "1rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  color: "#52009b",
                  borderLeft: "1px solid rgba(82,0,155,0.2)",
                  "&:hover": { bgcolor: "rgba(82,0,155,0.06)" },
                }}
              >
                +
              </Box>
            </Box>
          )}
          {viewMode === "classic" && (scale !== 1 || pan.x !== 0 || pan.y !== 0) && (
            <MDButton
              size="small"
              variant="text"
              onClick={resetView}
              sx={{ color: "#52009b", textTransform: "none", fontSize: "0.7rem", minWidth: 0 }}
            >
              Reset
            </MDButton>
          )}
          {onCreateRisk && (
            <MDButton
              size="small"
              variant="contained"
              onClick={onCreateRisk}
              sx={{
                bgcolor: "#52009b",
                color: "#f7f4fb",
                textTransform: "none",
                "&:hover": { bgcolor: "#3d0075" },
              }}
            >
              + Add Risk
            </MDButton>
          )}
          <MDButton
            size="small"
            variant="outlined"
            onClick={load}
            sx={{ color: "#52009b", borderColor: "#52009b", textTransform: "none" }}
          >
            Refresh
          </MDButton>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#52009b" }} />
        </Box>
      )}
      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* ── Radar oval (fixed width so it never reflows on hover) ── */}
            <Grid item xs={12} md={7}>
              {viewMode === "classic" ? (
                <Box
                  ref={radarBoxRef}
                  sx={{
                    position: "relative",
                    border: "1px solid rgba(82,0,155,0.15)",
                    borderRadius: 2,
                    bgcolor: "#fff",
                    cursor: dragRef.current.active ? "grabbing" : "grab",
                    userSelect: "none",
                    overflow: "hidden",
                  }}
                  onMouseDown={onMouseDown}
                  onMouseMove={onMouseMove}
                  onMouseUp={onMouseUp}
                  onMouseLeave={onMouseUp}
                  onDoubleClick={(e) => zoomToward(1.8, e.clientX, e.clientY)}
                >
                  <svg
                    ref={svgRef}
                    viewBox={`${vbLeft} ${vbTop} ${vbW} ${vbH}`}
                    style={{ width: "100%", display: "block" }}
                    aria-label="Emerging risk radar"
                  >
                    <defs>
                      <marker
                        id="velArrow"
                        markerWidth="6"
                        markerHeight="6"
                        refX="4"
                        refY="3"
                        orient="auto"
                        markerUnits="strokeWidth"
                      >
                        <path d="M0,0 L5,3 L0,6 Z" fill="#f97316" />
                      </marker>
                      {/* 3-D sphere gradients */}
                      <radialGradient id="sphereThreat" cx="35%" cy="28%" r="75%">
                        <stop offset="0%" stopColor="#ffb4ab" />
                        <stop offset="55%" stopColor="#ef4444" />
                        <stop offset="100%" stopColor="#9f1239" />
                      </radialGradient>
                      <radialGradient id="sphereOpp" cx="35%" cy="28%" r="75%">
                        <stop offset="0%" stopColor="#bfdbfe" />
                        <stop offset="55%" stopColor="#3b82f6" />
                        <stop offset="100%" stopColor="#1e3a8a" />
                      </radialGradient>
                    </defs>
                    {/* ── Radar disc fill (gray) ── */}
                    <ellipse cx={CX} cy={CY} rx={RX} ry={Math.abs(ryEff)} fill="#ededed" />
                    {/* ── Background rings ── */}
                    {RINGS.map((ring) => (
                      <ellipse
                        key={ring.r}
                        cx={CX}
                        cy={CY}
                        rx={ring.r * RX}
                        ry={ring.r * Math.abs(ryEff)}
                        fill="none"
                        stroke={ring.r === 1 ? "rgba(82,0,155,0.25)" : "rgba(82,0,155,0.12)"}
                        strokeWidth={ring.r === 1 ? 2 : 1}
                        strokeDasharray={ring.r < 1 ? "6 4" : "none"}
                      />
                    ))}

                    {/* ── Ring labels (Near / Medium / Long) ── */}
                    {RINGS.map((ring) => {
                      const ringLabelPt = pt(Math.PI / 2 + rotZ, ring.r);
                      return (
                        <text
                          key={`rl-${ring.r}`}
                          x={ringLabelPt.x + 6}
                          y={ringLabelPt.y + 4}
                          fontSize={11}
                          fill="rgba(82,0,155,0.45)"
                          fontWeight={600}
                        >
                          {ring.label}
                        </text>
                      );
                    })}

                    {/* ── Category dividers & labels ── */}
                    {categories.map((cat) => {
                      // Focus mode: keep ALL spokes visible; the non-focused ones are just dimmed
                      // so the selected risk's category stands out without hiding the rest.
                      const spokeDim = !exporting && focusCat && cat !== focusCat;
                      const angle = catAngle(cat) + rotZ;
                      const inner = pt(angle, 0.1);
                      const outer = pt(angle, 1.08);
                      const labelPt = pt(angle, 1.18);
                      const subPt = pt(angle, 1.18);
                      const showFocusL2 =
                        focusCat === cat && focusRisk?.subCategory && scale >= 1.4;
                      return (
                        <g key={cat} opacity={spokeDim ? 0.25 : 1}>
                          <line
                            x1={inner.x}
                            y1={inner.y}
                            x2={outer.x}
                            y2={outer.y}
                            stroke="rgba(82,0,155,0.15)"
                            strokeWidth={1}
                          />
                          {/* L1 category label — shown when in view */}
                          {inView(labelPt.x, labelPt.y) && (
                            <text
                              x={labelPt.x}
                              y={labelPt.y}
                              fontSize={12}
                              fontWeight={700}
                              fill="#52009b"
                              textAnchor="middle"
                              dominantBaseline="middle"
                              data-fulllabel={cat}
                            >
                              {cat.length > 18 ? `${cat.slice(0, 16)}…` : cat}
                            </text>
                          )}
                          {/* L2 (sub-category) of the focused risk — only when focused & zoomed */}
                          {showFocusL2 && inView(subPt.x, subPt.y) && (
                            <text
                              x={subPt.x}
                              y={subPt.y + 16}
                              fontSize={11}
                              fontWeight={600}
                              fill="#7b1fa2"
                              textAnchor="middle"
                              dominantBaseline="middle"
                            >
                              {focusRisk.subCategory.length > 22
                                ? `${focusRisk.subCategory.slice(0, 20)}…`
                                : focusRisk.subCategory}
                            </text>
                          )}
                        </g>
                      );
                    })}

                    {/* ── Risk dots ── */}
                    {risks.map((risk, i) => {
                      // Click-to-focus: when a dot is selected, keep the others on screen but
                      // dimmed/subtle so the selected risk stands out (export renders all at full).
                      const dim = !exporting && selected && risk.id !== selected;
                      const angle = angles[i] + rotZ;
                      const { rFrac } = layout[i];
                      const { x, y } = pt(angle, rFrac);

                      // Bar colours by direction: optimistic (top, upside) = blue,
                      // pessimistic (bottom, downside) = red.
                      const optColor = OPP_COLOR;
                      const pessColor = THREAT_COLOR;
                      const isActive = selected === risk.id || hovered === risk.id;

                      // Bar heights (scaled to maxVal). barDir = cos(rotX) so the pole
                      // leans with the plane tilt — positive = up, negative when flipped.
                      // A non-zero value always gets at least MIN_BAR_H px so small-consequence
                      // risks still show a visible blue/red bar next to much larger ones.
                      const scaleBar = (v) => {
                        if (!v) return 0;
                        const h = (v / maxVal) * BAR_MAX_H;
                        return Math.max(h, MIN_BAR_H) * Math.abs(barDir);
                      };
                      const optH = scaleBar(risk.scenarios.optimistic.value);
                      const pessH = scaleBar(risk.scenarios.pessimistic.value);
                      const barTop = y - optH * Math.sign(barDir || 1);
                      const barBot = y + pessH * Math.sign(barDir || 1);

                      // Ball position: 1 = at optimistic (top), 0 = at pessimistic (bottom).
                      // signpostProximity from API: triggered≈0.1, imminent≈0.2, approaching≈0.4, far≈0.6
                      const prox = risk.signpostProximity ?? 0.5;
                      const ballY = barBot - (barBot - barTop) * prox;
                      // Ball size = the consequence value for the ball's relative position,
                      // anchored on the actual scenario values: optimistic (prox=1),
                      // base/trending (prox=0.5), pessimistic (prox=0). Falls back to a
                      // 2-point optimistic↔pessimistic blend when there's no base value
                      // (so a high-threat risk isn't tiny just because base is missing).
                      const optVal = risk.scenarios.optimistic.value || 0;
                      const baseVal = risk.scenarios.base.value || 0;
                      const pessVal = risk.scenarios.pessimistic.value || 0;
                      let ballValue;
                      if (baseVal > 0) {
                        ballValue =
                          prox >= 0.5
                            ? baseVal + (optVal - baseVal) * ((prox - 0.5) / 0.5)
                            : pessVal + (baseVal - pessVal) * (prox / 0.5);
                      } else {
                        ballValue = optVal * prox + pessVal * (1 - prox);
                      }
                      const ballR = Math.max(8, Math.min(22, 8 + (ballValue / maxVal) * 14));

                      // Extend each coloured segment so it pokes out past the ball's edge — otherwise
                      // a short top/bottom half is hidden behind the ball (e.g. a 30% optimistic cap).
                      const dir = Math.sign(barDir || 1);
                      const cap = ballR + 7;
                      const barTopVis =
                        dir >= 0 ? Math.min(barTop, ballY - cap) : Math.max(barTop, ballY + cap);
                      const barBotVis =
                        dir >= 0 ? Math.max(barBot, ballY + cap) : Math.min(barBot, ballY - cap);

                      const icon = alertIcon(risk.signpostAlert, risk.signpostDirection);
                      // Velocity arrow length: fast (High) = long, slow (Low) = short
                      const vLow = (risk.velocity || "").toLowerCase();
                      const isFast = vLow.includes("fast") || vLow.includes("high");
                      const isSlow = vLow.includes("slow") || vLow.includes("low");
                      const vLen = isFast ? 22 : isSlow ? 9 : 15;

                      // Velocity arrow always points toward the centre of the radar
                      const cdx = CX - x;
                      const cdy = CY - ballY;
                      const cd = Math.hypot(cdx, cdy) || 1;
                      const ux = cdx / cd;
                      const uy = cdy / cd;
                      const aStart = ballR + 3;
                      const ax1 = x + ux * aStart;
                      const ay1 = ballY + uy * aStart;
                      const ax2 = x + ux * (aStart + vLen);
                      const ay2 = ballY + uy * (aStart + vLen);

                      // Ball/glow/label colour by trend direction: blue when trending toward the
                      // optimistic (upside) half, red toward the pessimistic (downside) half.
                      const towardOptimistic = prox >= 0.5;
                      const color = towardOptimistic ? OPP_COLOR : THREAT_COLOR;
                      const sphereFill = towardOptimistic
                        ? "url(#sphereOpp)"
                        : "url(#sphereThreat)";

                      return (
                        <g
                          key={risk.id}
                          style={{ cursor: "pointer" }}
                          opacity={dim ? 0.28 : 1}
                          onMouseEnter={() => setHovered(risk.id)}
                          onMouseLeave={() => setHovered(null)}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRiskClick(risk);
                          }}
                        >
                          {/* Glow ring when active */}
                          {isActive && <circle cx={x} cy={y} r={16} fill={color} opacity={0.15} />}

                          {/* Scenario bar split AT THE BALL: blue above (headroom to the
                            optimistic top), red below (downside to the pessimistic bottom).
                            Ends extend past the ball so both colours stay visible. */}
                          <line
                            x1={x}
                            y1={barTopVis}
                            x2={x}
                            y2={ballY}
                            stroke={optColor}
                            strokeWidth={isActive ? 4 : 3}
                            strokeLinecap="round"
                          />
                          <line
                            x1={x}
                            y1={ballY}
                            x2={x}
                            y2={barBotVis}
                            stroke={pessColor}
                            strokeWidth={isActive ? 4 : 3}
                            strokeLinecap="round"
                          />

                          {/* Velocity arrow (orange) — points toward centre, length = speed */}
                          <line
                            x1={ax1}
                            y1={ay1}
                            x2={ax2}
                            y2={ay2}
                            stroke="#f97316"
                            strokeWidth={2.5}
                            markerEnd="url(#velArrow)"
                          />

                          {/* Ball (current signpost position) — 3-D sphere */}
                          <circle
                            cx={x}
                            cy={ballY}
                            r={ballR}
                            fill={sphereFill}
                            stroke="#fff"
                            strokeWidth={1.5}
                          />

                          {/* Number on the ball — always shown in export mode, otherwise only when
                            zoomed in enough to read it. Matches the numbered list in the export. */}
                          {(exporting || scale >= 1.6) && risk.displayNo != null && (
                            <text
                              x={x}
                              y={ballY}
                              fontSize={exporting ? Math.max(11, ballR) : Math.max(9, ballR)}
                              fontWeight={800}
                              fill="#fff"
                              textAnchor="middle"
                              dominantBaseline="central"
                              style={{ pointerEvents: "none" }}
                            >
                              {risk.displayNo}
                            </text>
                          )}

                          {/* Alert icon — triangle (breached) or flag (approaching) */}
                          {icon &&
                            (() => {
                              const ix = x - ballR - 14;
                              const iy = ballY - 4;
                              const ic = ALERT_COLORS[risk.signpostAlert] || "#9ca3af";
                              if (icon === "⚠") {
                                // Filled warning triangle
                                return (
                                  <polygon
                                    points={`${ix},${iy + 10} ${ix - 8},${iy + 10} ${ix - 4},${
                                      iy + 2
                                    }`}
                                    fill={ic}
                                  />
                                );
                              }
                              // Flag for approaching/imminent
                              return (
                                <g>
                                  <line
                                    x1={ix - 7}
                                    y1={iy + 2}
                                    x2={ix - 7}
                                    y2={iy + 12}
                                    stroke={ic}
                                    strokeWidth={1.5}
                                  />
                                  <polygon
                                    points={`${ix - 7},${iy + 2} ${ix},${iy + 4} ${ix - 7},${
                                      iy + 7
                                    }`}
                                    fill={ic}
                                  />
                                </g>
                              );
                            })()}

                          {/* Risk name is shown in the side detail panel on hover/select, not on
                              the chart. In export mode the numbered list maps balls → titles. */}

                          {/* L2 (sub-category) revealed on zoom — one label per sub-category. */}
                          {!exporting &&
                            !focusRisk &&
                            scale >= 1.6 &&
                            risk.subCategory &&
                            l2RepRiskIds.has(risk.id) && (
                              <text
                                x={x}
                                y={Math.min(barTopVis, barBotVis) - 8}
                                fontSize={10}
                                fontWeight={600}
                                fill="#7b1fa2"
                                textAnchor="middle"
                                style={{ pointerEvents: "none", paintOrder: "stroke" }}
                                stroke="#fff"
                                strokeWidth={3}
                              >
                                {risk.subCategory.length > 22
                                  ? `${risk.subCategory.slice(0, 20)}…`
                                  : risk.subCategory}
                              </text>
                            )}
                        </g>
                      );
                    })}

                    {/* Centre dot */}
                    <circle cx={CX} cy={CY} r={4} fill="rgba(82,0,155,0.3)" />

                    {/* Drag hint */}
                    <text x={12} y={H - 10} fontSize={10} fill="rgba(0,0,0,0.3)">
                      Drag to rotate
                    </text>
                  </svg>

                  {/* Consequence-size scales — ball size encodes consequence. Quantitative scale
                      bottom-left, qualitative scale bottom-right. */}
                  {[
                    { side: "left", title: "Financial", range: "$ low → high" },
                    { side: "right", title: "Level", range: "Level 1 → 6" },
                  ].map((sc) => (
                    <Box
                      key={sc.side}
                      sx={{
                        position: "absolute",
                        [sc.side]: 10,
                        bottom: 52,
                        background: "rgba(255,255,255,0.92)",
                        border: "1px solid #e3e8ec",
                        borderRadius: "8px",
                        p: "6px 9px",
                        pointerEvents: "none",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "0.6rem",
                          fontWeight: 700,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                          color: "#5B6B78",
                          marginBottom: 4,
                        }}
                      >
                        {sc.title}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        {[6, 9, 12].map((d) => (
                          <span
                            key={d}
                            style={{
                              width: d,
                              height: d,
                              borderRadius: "50%",
                              background: "#90a4ae",
                              display: "inline-block",
                            }}
                          />
                        ))}
                        <span style={{ fontSize: "0.62rem", color: "#5B6B78", marginLeft: 2 }}>
                          {sc.range}
                        </span>
                      </div>
                    </Box>
                  ))}

                  {/* Legend */}
                  <Box sx={{ display: "flex", gap: 2, px: 2, pb: 1.5, flexWrap: "wrap" }}>
                    {[
                      { color: THREAT_COLOR, label: "Threat / loss" },
                      { color: OPP_COLOR, label: "Opportunity / gain" },
                    ].map((l) => (
                      <Box key={l.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 3, height: 18, bgcolor: l.color, borderRadius: 1 }} />
                        <Typography variant="caption" color="text.secondary">
                          {l.label}
                        </Typography>
                      </Box>
                    ))}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ⚠ signpost breached
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ⚑ signpost approaching
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ● current estimate (size = consequence)
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Box
                        sx={{ width: 16, height: 2.5, bgcolor: "#f97316", position: "relative" }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            right: -3,
                            top: -2,
                            width: 0,
                            height: 0,
                            borderTop: "3px solid transparent",
                            borderBottom: "3px solid transparent",
                            borderLeft: "5px solid #f97316",
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        velocity (longer = faster)
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Bar top = best case · bottom = worst case
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <DualPlaneRadar
                  risks={risks}
                  taxonomy={taxonomy}
                  selected={selected}
                  hovered={hovered || previewId}
                  onSelect={handleRiskClick}
                  onHover={setHovered}
                  plausibilityMode={plausibilityMode}
                  likelihoodBands={likelihoodBands}
                  consequenceLevels={consequenceLevels}
                />
              )}
            </Grid>

            {/* ── Detail panel (column always present so radar never reflows) ── */}
            <Grid item xs={12} md={5}>
              {!activeRisk ? (
                <Box
                  sx={{
                    border: "1px dashed rgba(82,0,155,0.2)",
                    borderRadius: 2,
                    p: 3,
                    height: "100%",
                    minHeight: 280,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <MDTypography variant="body2" color="text.secondary">
                    Hover over a risk to preview it, or click to keep it open.
                  </MDTypography>
                </Box>
              ) : (
                <Box
                  sx={{
                    border: "1px solid rgba(82,0,155,0.2)",
                    borderRadius: 2,
                    p: 2,
                    position: "relative",
                    height: "100%",
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelected(null);
                      setHovered(null);
                    }}
                    sx={{ position: "absolute", top: 8, right: 8, color: "#9ca3af" }}
                  >
                    ✕
                  </IconButton>

                  {/* Title + chips */}
                  <MDTypography variant="h6" fontWeight="bold" sx={{ mb: 1, pr: 3 }}>
                    {activeRisk.title}
                  </MDTypography>
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1.5 }}>
                    <Chip
                      size="small"
                      label={activeRisk.category}
                      sx={{ bgcolor: "#ede7f6", color: "#52009b", fontWeight: 600, fontSize: 11 }}
                    />
                    {activeRisk.subCategory && (
                      <Chip
                        size="small"
                        label={activeRisk.subCategory}
                        sx={{ bgcolor: "#f3e5f5", color: "#7b1fa2", fontSize: 11 }}
                      />
                    )}
                    <Chip
                      size="small"
                      label={activeRisk.timeFrame?.replace("-term", "") || "medium"}
                      sx={{ bgcolor: "#e8eaf6", color: "#3949ab", fontSize: 11 }}
                    />
                    {activeRisk.velocity && (
                      <Chip
                        size="small"
                        label={`${activeRisk.velocity} velocity`}
                        sx={{ bgcolor: "#e0f2f1", color: "#00695c", fontSize: 11 }}
                      />
                    )}
                  </Box>

                  {/* Alert status */}
                  {activeRisk.signpostAlert !== "none" && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        mb: 1.5,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1,
                        bgcolor: activeRisk.signpostAlert === "red" ? "#ffebee" : "#fff8e1",
                      }}
                    >
                      <Typography sx={{ fontSize: "1rem" }}>⚠</Typography>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ color: activeRisk.signpostAlert === "red" ? "#b71c1c" : "#f57f17" }}
                      >
                        {activeRisk.signpostDirection === "triggered"
                          ? "Signpost threshold breached"
                          : activeRisk.signpostDirection === "imminent"
                          ? "Signpost breach imminent"
                          : "Signpost approaching threshold"}
                      </Typography>
                    </Box>
                  )}

                  {activeRisk.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5, fontSize: "0.8rem", lineHeight: 1.6 }}
                    >
                      {activeRisk.description.length > 300
                        ? `${activeRisk.description.slice(0, 300)}…`
                        : activeRisk.description}
                    </Typography>
                  )}

                  <Divider sx={{ mb: 1.5 }} />

                  {/* Scenario table */}
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "rgba(82,0,155,0.04)" }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#52009b" }}>
                          {" "}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#52009b" }}>
                          Value
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#52009b" }}>
                          Preparedness
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#52009b" }}>
                          Velocity
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const rows = activeRisk.scenarioRows || [];
                        const prox = activeRisk.signpostProximity ?? 0.5;
                        // Trending interpolates across the FULL range of ALL scenarios: the lowest
                        // value (best/optimistic end) ↔ the highest value (worst/pessimistic end),
                        // positioned by the live signpost proximity (1 = best, 0 = worst).
                        const vals = rows.map((s) => s.value).filter((v) => v > 0);
                        const minV = vals.length ? Math.min(...vals) : 0;
                        const maxV = vals.length ? Math.max(...vals) : 0;
                        const trendEst = minV * prox + maxV * (1 - prox);
                        const overallVel =
                          rows[0]?.velocity ||
                          activeRisk.velocity ||
                          activeRisk.scenarios?.base?.velocity ||
                          "";
                        // Best = green, Worst = red, any custom scenario = neutral.
                        const colorFor = (lbl) =>
                          /worst/i.test(lbl)
                            ? "#b71c1c"
                            : /best/i.test(lbl)
                            ? "#2e7d32"
                            : "#374151";
                        // ID82 — show the final value (non-financial qual → "Type Level"; else $ +
                        // QUAL chip), with an info icon whose tooltip breaks down the calculation.
                        const valueCell = (sc) => {
                          const qualText = `${sc.consequenceType || ""} ${
                            sc.qualitative || ""
                          }`.trim();
                          const showQualText =
                            sc.valueSource === "qualitative" && !sc.qualFinancial && !!qualText;
                          const tip = (
                            <Box sx={{ fontSize: "0.72rem", lineHeight: 1.4 }}>
                              <div>
                                Qual: {qualText || "—"}{" "}
                                {sc.qualDollar > 0
                                  ? `(→ ${fmtMoney(sc.qualDollar)})`
                                  : "(no $ at this level)"}
                              </div>
                              <div>Quant: {sc.quant > 0 ? fmtMoney(sc.quant) : "—"}</div>
                              <div>
                                Used: {fmtMoney(sc.value)} ({sc.valueSource}
                                {sc.qualFinancial ? ", financial type" : ""})
                              </div>
                            </Box>
                          );
                          return (
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.3 }}>
                              {showQualText ? (
                                <span>{qualText}</span>
                              ) : (
                                <span>
                                  {fmtMoney(sc.value)}
                                  {sc.valueSource === "qualitative" && (
                                    <Box
                                      component="span"
                                      sx={{
                                        ml: 0.5,
                                        px: "4px",
                                        py: "1px",
                                        borderRadius: "3px",
                                        fontSize: "0.58rem",
                                        fontWeight: 700,
                                        bgcolor: "#ede7f6",
                                        color: "#52009b",
                                        border: "1px solid #ce93d8",
                                        verticalAlign: "middle",
                                      }}
                                    >
                                      QUAL
                                    </Box>
                                  )}
                                </span>
                              )}
                              <Tooltip title={tip} arrow placement="top">
                                <InfoOutlinedIcon
                                  sx={{ fontSize: 14, color: "#9e9e9e", cursor: "help" }}
                                />
                              </Tooltip>
                            </Box>
                          );
                        };
                        const scenarioRow = (sc, k) => (
                          <TableRow key={k}>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.72rem",
                                color: colorFor(sc.label),
                              }}
                            >
                              {sc.label}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.72rem" }}>{valueCell(sc)}</TableCell>
                            <TableCell sx={{ fontSize: "0.72rem" }}>
                              {sc.preparedness || "—"}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.72rem" }}>{sc.velocity || "—"}</TableCell>
                          </TableRow>
                        );
                        const trendingRow = (
                          <TableRow key="trending">
                            <TableCell
                              sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#1565c0" }}
                            >
                              Current State
                            </TableCell>
                            <TableCell
                              sx={{ fontSize: "0.72rem" }}
                              title="Where the risk is currently trending — the live signpost position between the Best and Worst limits"
                            >
                              {trendEst > 0 ? `~${fmtMoney(trendEst)}` : "—"}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.72rem" }}>—</TableCell>
                            <TableCell sx={{ fontSize: "0.72rem" }}>{overallVel || "—"}</TableCell>
                          </TableRow>
                        );
                        const best = rows[0];
                        const worst = rows[1];
                        const extra = rows.slice(2);
                        return (
                          <>
                            {best && scenarioRow(best, "sc-best")}
                            {trendingRow}
                            {worst && scenarioRow(worst, "sc-worst")}
                            {extra.map((sc, i) => scenarioRow(sc, `sc-extra-${i}`))}
                          </>
                        );
                      })()}
                    </TableBody>
                  </Table>

                  {/* Action buttons */}
                  <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
                    <MDButton
                      size="small"
                      variant="outlined"
                      endIcon={<OpenInNewIcon sx={{ fontSize: "0.9rem !important" }} />}
                      onClick={() => {
                        if (onOpenRisk) onOpenRisk(activeRisk);
                        else navigate(`/anticipate/emerging-risk?id=${activeRisk.id}`);
                      }}
                      sx={{
                        color: "#52009b",
                        borderColor: "#52009b",
                        textTransform: "none",
                        fontSize: "0.72rem",
                      }}
                    >
                      Open full record
                    </MDButton>
                    {activeRisk.linkedHorizonTheme && (
                      <MDButton
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          navigate(
                            `/anticipate/HorizonScan?theme=${encodeURIComponent(
                              activeRisk.linkedHorizonTheme
                            )}`
                          )
                        }
                        sx={{
                          color: "#374151",
                          borderColor: "#d1d5db",
                          textTransform: "none",
                          fontSize: "0.72rem",
                        }}
                      >
                        See Horizon Scan stream
                      </MDButton>
                    )}
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>

          {/* ── Watch List table ── */}
          {(risks.length > 0 || draftRisks.length > 0) && (
            <Box
              sx={{
                border: "1px solid rgba(82,0,155,0.15)",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: "rgba(82,0,155,0.06)",
                  borderBottom: "1px solid rgba(82,0,155,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <MDTypography variant="subtitle2" fontWeight="bold">
                  Watch List Records
                </MDTypography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {draftRisks.length > 0 && (
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={showDrafts}
                          onChange={(e) => setShowDrafts(e.target.checked)}
                        />
                      }
                      label={
                        <MDTypography variant="caption">
                          Show drafts ({draftRisks.length})
                        </MDTypography>
                      }
                      sx={{ m: 0 }}
                    />
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={showArchived}
                        onChange={(e) => {
                          setShowArchived(e.target.checked);
                          if (e.target.checked) loadArchived();
                        }}
                      />
                    }
                    label={
                      <MDTypography variant="caption">
                        Show archived{archivedRisks.length ? ` (${archivedRisks.length})` : ""}
                      </MDTypography>
                    }
                    sx={{ m: 0 }}
                  />
                </Box>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "rgba(82,0,155,0.03)" }}>
                    <SortableTh col="risk" sort={tableSort} onSort={handleTableSort}>
                      Risk
                    </SortableTh>
                    <SortableTh col="timeframe" sort={tableSort} onSort={handleTableSort}>
                      Timeframe
                    </SortableTh>
                    <SortableTh col="velocity" sort={tableSort} onSort={handleTableSort}>
                      Velocity
                    </SortableTh>
                    <SortableTh col="signposts" sort={tableSort} onSort={handleTableSort}>
                      Signposts
                    </SortableTh>
                    <SortableTh col="alert" sort={tableSort} onSort={handleTableSort}>
                      Alert
                    </SortableTh>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortRiskRows(showDrafts ? [...risks, ...draftRisks] : risks, tableSort).map(
                    (r) => {
                      const isDraft = r.status !== "active";
                      const col = r.threatOpp === "opportunity" ? OPP_COLOR : THREAT_COLOR;
                      const spIcon = alertIcon(r.signpostAlert, r.signpostDirection);
                      const alertBg =
                        r.signpostAlert === "red"
                          ? "#dc2626"
                          : r.signpostAlert === "amber"
                          ? "#d97706"
                          : null;
                      return (
                        <TableRow
                          key={r.id}
                          hover
                          sx={{
                            cursor: "pointer",
                            opacity: isDraft ? 0.6 : 1,
                            bgcolor: selected === r.id ? "rgba(82,0,155,0.04)" : undefined,
                          }}
                          // Clicking a table row opens the full record directly (the radar dots still
                          // just select/preview). Falls back to selecting if no open handler is wired.
                          onClick={() => (onOpenRisk ? onOpenRisk(r) : handleRiskClick(r))}
                        >
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: "50%",
                                  bgcolor: col,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{ fontSize: "0.8rem" }}
                              >
                                {r.title}
                              </Typography>
                              {isDraft && (
                                <Chip
                                  label="Draft"
                                  size="small"
                                  sx={{ height: 18, fontSize: "0.65rem", ml: 0.5 }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {r.timeFrame?.replace("-term", "") || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Typography sx={{ fontSize: "0.9rem" }}>
                                {r.velocity?.toLowerCase().includes("fast") ? "→→" : "→"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {r.velocity || "—"}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {spIcon ? (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography sx={{ fontSize: "0.85rem" }}>{spIcon}</Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: "0.7rem" }}
                                >
                                  {r.signpostDirection === "triggered"
                                    ? "Exceeding"
                                    : r.signpostDirection === "approaching" ||
                                      r.signpostDirection === "imminent"
                                    ? "Approaching"
                                    : ""}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.disabled">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {alertBg ? (
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  px: "8px",
                                  py: "2px",
                                  borderRadius: "4px",
                                  bgcolor: alertBg,
                                  color: "#fff",
                                  fontSize: "0.68rem",
                                  fontWeight: 700,
                                }}
                              >
                                {r.signpostAlert.toUpperCase()}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.disabled">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    }
                  )}
                </TableBody>
              </Table>
              {/* ID195 — archived (closed) risks + restore, shown under the Watch List table */}
              {showArchived && (
                <Box
                  sx={{
                    m: 1.5,
                    p: 1.5,
                    border: "1px solid #e7e2ef",
                    borderRadius: 2,
                    bgcolor: "#faf9fc",
                  }}
                >
                  <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#6a6478" }}>
                    Archived risks ({archivedRisks.length})
                  </MDTypography>
                  {archivedRisks.length === 0 ? (
                    <MDTypography
                      variant="caption"
                      sx={{ display: "block", mt: 0.5 }}
                      color="text.secondary"
                    >
                      No archived risks.
                    </MDTypography>
                  ) : (
                    archivedRisks.map((ar) => (
                      <Box
                        key={ar.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          py: 0.5,
                          borderBottom: "1px solid #efeaf6",
                        }}
                      >
                        <MDTypography variant="caption" sx={{ flex: 1 }}>
                          {ar.Title || "(untitled risk)"}
                          {ar.closedReason ? (
                            <span style={{ color: "#8a6300" }}> · {ar.closedReason}</span>
                          ) : null}
                        </MDTypography>
                        <MDButton
                          size="small"
                          variant="text"
                          startIcon={<RestoreIcon fontSize="small" />}
                          sx={{ color: "#2c7a50" }}
                          onClick={() => handleRestore(ar.id)}
                        >
                          Restore
                        </MDButton>
                      </Box>
                    ))
                  )}
                </Box>
              )}
            </Box>
          )}

          {risks.length === 0 && (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <MDTypography variant="body2" color="text.secondary">
                The radar shows all risk categories. No active emerging risks are linked yet — add
                or activate a risk to plot it against its category.
              </MDTypography>
            </Box>
          )}
        </>
      )}
    </Box>
  );

  if (embedded) return content;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {content}
    </DashboardLayout>
  );
}

EmergingRiskRadar.propTypes = {
  embedded: PropTypes.bool,
  onOpenRisk: PropTypes.func,
  onCreateRisk: PropTypes.func,
};

EmergingRiskRadar.defaultProps = {
  embedded: false,
  onOpenRisk: null,
  onCreateRisk: null,
};
